import opentype from 'opentype.js';

const fontCache: Record<string, opentype.Font> = {};
const loadingPromises: Record<string, Promise<opentype.Font | null>> = {};

/** Fixed OpenType size for path generation and viewBox measurement. */
export const PATH_FONT_SIZE = 12;

/**
 * Canvas mapping: one path unit at PATH_FONT_SIZE equals one centimeter (10 mm).
 * Natural mm size = viewBoxWidth × PATH_UNIT_TO_MM.
 */
export const PATH_UNIT_TO_MM = 10;

export const DEFAULT_TEXT_BASE_WIDTH = PATH_FONT_SIZE * 0.3;
export const DEFAULT_TEXT_BASE_HEIGHT = PATH_FONT_SIZE * 0.3;

export async function loadOpentypeFont(fontUrl: string): Promise<opentype.Font | null> {
  if (fontCache[fontUrl]) return fontCache[fontUrl];
  if (loadingPromises[fontUrl]) return loadingPromises[fontUrl];

  loadingPromises[fontUrl] = (async () => {
    try {
      const response = await fetch(fontUrl);
      if (!response.ok) return null;
      const buffer = await response.arrayBuffer();
      const font = opentype.parse(buffer);
      fontCache[fontUrl] = font;
      return font;
    } catch {
      return null;
    } finally {
      delete loadingPromises[fontUrl];
    }
  })();

  return loadingPromises[fontUrl];
}

export type TextPathLayout = {
  pathData: string;
  viewBoxWidth: number;
  viewBoxHeight: number;
  offsetX: number;
  offsetY: number;
  pathFontSize: number;
};

function buildTextPathLayout(font: opentype.Font, content: string): TextPathLayout {
  const pathFontSize = PATH_FONT_SIZE;
  const unitsPerEm = font.unitsPerEm || 1000;
  const baselineY = (font.ascender / unitsPerEm) * pathFontSize;
  const text = content || ' ';

  let cursorX = 0;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  const paths: string[] = [];

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    // Round the glyph origin to 2 decimals before generating the path.
    // Accumulating advance widths drifts cursorX into ugly floats (e.g.
    // 46.87200000000001). opentype.js v2.0.0's toPathData() then formats a
    // coordinate whose fractional part stringifies in scientific notation,
    // which its rounding mishandles into "NaN". A single NaN in the `d`
    // attribute makes the browser stop rendering the rest of the path, so later
    // glyphs vanish while their advance-width whitespace remains (font- and
    // length-dependent, e.g. Amatic SC truncating long text). Rounding the
    // origin removes the drift that triggers the bug.
    //
    // @todo Remove this workaround once opentype.js > 2.0.0 is released. The bug
    // is fixed upstream (opentypejs/opentype.js#673, merged via PR #839) but
    // unreleased — npm's latest is still 2.0.0. When a newer version ships, run
    // `npm update opentype.js` (package.json's "^2.0.0" already allows it),
    // confirm the fix is in it, then drop this rounding and pass `cursorX`
    // straight to font.getPath() again.
    const glyphX = Math.round(cursorX * 100) / 100;
    const path = font.getPath(char, glyphX, baselineY, pathFontSize);
    const box = path.getBoundingBox();

    if (char.trim() !== '' && box.x2 > box.x1 && box.y2 > box.y1) {
      minX = Math.min(minX, box.x1);
      minY = Math.min(minY, box.y1);
      maxX = Math.max(maxX, box.x2);
      maxY = Math.max(maxY, box.y2);
    }

    paths.push(path.toPathData(2));
    cursorX += font.getAdvanceWidth(char, pathFontSize);
  }

  if (!Number.isFinite(minX)) {
    minX = 0;
    maxX = pathFontSize * 0.3;
    minY = 0;
    maxY = pathFontSize * 0.3;
  }

  const calculatedWidth = maxX - minX;
  const bboxHeight = maxY - minY;
  const offsetX = -minX;
  const offsetY = -minY;

  return {
    pathData: paths.join(' '),
    viewBoxWidth: Math.max(calculatedWidth, pathFontSize * 0.05),
    viewBoxHeight: Math.max(bboxHeight, pathFontSize * 0.05),
    offsetX,
    offsetY,
    pathFontSize,
  };
}

export function buildTextPathLayoutAtFontSize(
  font: opentype.Font,
  content: string
): TextPathLayout {
  const trimmed = (content || '').trim();
  if (!trimmed) {
    return {
      pathData: '',
      viewBoxWidth: DEFAULT_TEXT_BASE_WIDTH,
      viewBoxHeight: DEFAULT_TEXT_BASE_HEIGHT,
      offsetX: 0,
      offsetY: 0,
      pathFontSize: PATH_FONT_SIZE,
    };
  }
  return buildTextPathLayout(font, content);
}

/** Map display width in mm to path-unit scale for canvas rendering. */
export function getTextRenderScale(itemWidthMm: number, viewBoxWidth: number): number {
  if (viewBoxWidth <= 0) return 1;
  return itemWidthMm / viewBoxWidth;
}

export function worldToScreen(
  worldX: number,
  worldY: number,
  viewRect: { x: number; y: number },
  pxPerMm: number
): { x: number; y: number } {
  return {
    x: (worldX - viewRect.x) * pxPerMm,
    y: (worldY - viewRect.y) * pxPerMm,
  };
}
